<script setup lang="ts">
import {Query, QueryNavigation} from '@/backend/queries';
import {Models, useGraphMeta, useLocalization} from '@hantera/portal-app';
import {DropdownButton, DropdownItem, RemoveButton, TextInput} from '@hantera/design-system';
import {computed, DeepReadonly, ref, watch} from 'vue';
import useGraphPhrases from '@/composition/use-graph-phrases';

const graphMeta = useGraphMeta();

const props = defineProps<{
  query: Query
  navigation: QueryNavigation
  edgesMeta: Record<string, Models.GraphMeta.GraphEdgeMeta>
  queryPath?: string[]
  displayManyWarning?: boolean
}>();

const t = useLocalization();
const graphPhrases = useGraphPhrases();
const selectEdgeFilter = ref<string>();

function clear() {

  // Remove related columns
  if (props.query.columns && props.navigation.edge) {
    const currentPath = (props.queryPath ?? []).concat([props.navigation.edge]).join('.');
    props.query.columns = props.query.columns.filter(col => !col.queryPath.join('.').startsWith(currentPath));
  }

  props.navigation.edge = undefined;
  props.navigation.navigation = undefined;
  props.navigation.filter = undefined;
}

function selectEdge(edge: string) {
  props.navigation.edge = edge;
}

const edgeMeta = computed<Models.GraphMeta.GraphEdgeMeta | undefined>(() => {
  if (!props.navigation.edge) {
    return;
  }
  return props.edgesMeta[props.navigation.edge];
});

const nodeMeta = computed<DeepReadonly<Models.GraphMeta.GraphNodeMeta> | undefined>(() => {
  if (!edgeMeta.value) {
    return;
  }
  return graphMeta[edgeMeta.value.node];
});

const newSubnavigation = ref<QueryNavigation>({})

watch(() => props.navigation?.navigation, () => {
  if (!props.navigation?.navigation) {
    return;
  }

  const toDelete: number[] = [];
  props.navigation.navigation.forEach((nav, idx) => {
    if (!nav.edge) {
      toDelete.push(idx);
    }
  });
  for (const idx of toDelete) {
    props.navigation.navigation.splice(idx, 1);
  }
}, {
  deep: true
});

watch(newSubnavigation, () => {
  if (newSubnavigation.value.edge) {
    if (!props.navigation.navigation) {
      props.navigation.navigation = [];
    }
    props.navigation.navigation.push(newSubnavigation.value);
    newSubnavigation.value = {};
  }
}, {
  deep: true
});

const newSubedges = computed(() => {
  if (!nodeMeta.value) {
    return {} as Record<string, Models.GraphMeta.GraphEdgeMeta>;
  }
  return Object.keys(nodeMeta.value.edges).reduce((res, edge) => {
    if (!props.navigation.navigation || props.navigation.navigation.findIndex(nav => nav.edge === edge) === -1) {
      const edgeMeta = nodeMeta.value?.edges[edge];
      if (edgeMeta) {
        res[edge] = edgeMeta;
      }
    }
    return res;
  }, {} as Record<string, Models.GraphMeta.GraphEdgeMeta>);

});

const subNavigations = computed(() => {
  if (!props.navigation.navigation) {
    return undefined;
  }
  let manyIdx = 0;
  return props.navigation.navigation.map(nav => ({
    nav: nav,
    manyIdx: nav.edge && isMany(nodeMeta.value?.edges[nav.edge]?.cardinality) ? manyIdx++ : 0
  }));

});

function isMany(cardinality?: Models.GraphMeta.GraphEdgeCardinality) {
  return cardinality === 'many';
}

const label = computed(() => {

  let currentNode = 'root';
  if (props.queryPath) {
    currentNode = Object.keys(graphMeta).find(r => graphMeta[r].rootEdgeName === props.queryPath![0])!;
    for (let i = 0; i < props.queryPath.length; i++) {
      const node = graphMeta[currentNode];
      if (node && i < props.queryPath.length - 1) {
        currentNode = node.edges[props.queryPath[i + 1]].node;
      }
    }
  }

  return graphPhrases.edge(currentNode, props.navigation.edge!);
});

function getEdgeLabel(edgeName: string)
{
  let currentNode = 'root';
  if (props.queryPath) {
    currentNode = Object.keys(graphMeta).find(r => graphMeta[r].rootEdgeName === props.queryPath![0])!;
    for (let i = 0; i < props.queryPath.length; i++) {
      const node = graphMeta[currentNode];
      if (node && i < props.queryPath.length - 1) {
        currentNode = node.edges[props.queryPath[i + 1]].node;
      }
    }
  }

  return graphPhrases.edge(currentNode, edgeName);
}

const sortedEdges = computed(() => {
  let list = Object.keys(props.edgesMeta).map(r => ({
    key: r,
    label: getEdgeLabel(r),
  }));
  
  if (selectEdgeFilter.value) {
    const needle = selectEdgeFilter.value.toLowerCase();
    
    list = list.filter(r => r.label.toLowerCase().indexOf(needle) !== -1);
  }
  
  list.sort((a, b) => {
    
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });
  
  return list;
});

</script>

<template>

  <div v-if="!edgeMeta" class="query-editor__select-edge">
    <DropdownButton :label="!queryPath ? 'Select from' : 'Add sub-query'">
      <div class="query-editor__select-edge-filter">
        <TextInput placeholder="Filter" v-model="selectEdgeFilter"/>
      </div>
      <DropdownItem v-for="entry in sortedEdges" :key="entry.key" @click="selectEdge(entry.key)">
        {{ entry.label }}
      </DropdownItem>
    </DropdownButton>
  </div>

  <div v-if="edgeMeta" class="query-editor__edge">
    {{ label }}
    
    <RemoveButton @click="clear()" />
  </div>

  <div v-if="edgeMeta && nodeMeta" class="query-editor__details">
    <div v-if="displayManyWarning" class="query-editor__many-warning">
      Warning: Only the first item will be displayed
    </div>
    <label>
      Filter:
      <TextInput v-model.lazy="navigation.filter" />
    </label>
  </div>

  <div v-if="navigation.edge && edgeMeta && nodeMeta" class="query-editor__navigations">

    <div
      v-for="subNavigation in subNavigations"
      :key="subNavigation.nav.edge"
      class="query-editor__sub-navigation"
    >
      <QueryEditorNavigation
        :query="query"
        :navigation="subNavigation.nav" :edges-meta="nodeMeta.edges"
        :query-path="(queryPath ?? []).concat([navigation.edge])"
        :display-many-warning="subNavigation.manyIdx > 0"
      />
    </div>

    <QueryEditorNavigation
      :query="query"
      v-if="Object.keys(newSubedges).length > 0"
      :navigation="newSubnavigation" :edges-meta="newSubedges"
      :query-path="(queryPath ?? []).concat([navigation.edge])"
    />

  </div>

</template>

<style>
.query-editor__select-edge,
.query-editor__edge {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin: calc(3px);
  height: 30px;
}

.query-editor__select-edge-filter {
  padding: 5px;
}

.query-editor__edge {
  border-bottom: 1px solid hsl(var(--color-60));

}

.query-editor__details {
  display: flex;
  flex-direction: column;
  margin: 0 0 0 0;
}

.query-editor__details input {
  max-width: 400px;
}

.query-editor__columns-label {
  margin-left: 3px;
}

.query-editor__navigations {
  margin-bottom: 1rem;
}

.query-editor__sub-navigation {
  padding-left: 10px;
  margin-left: 3px;
  border-left: 1px solid hsl(var(--color-60));
}

.query-editor__many-warning {
  color: var(--color-warning-50);
  margin: 0 3px;
}
</style>